<section class="flex flex-col gap-2 p-2" data-testid="product-vendor-transport-routes">
  <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
    <span class="text-muted-color">{{ 'transport-route.product.hint' | translate }}</span>
    <p-button
      class="ms-auto"
      icon="fa fa-plus"
      size="small"
      [outlined]="true"
      [label]="'transport-route.product.add' | translate"
      [disabled]="loading() || unusedRoutes().length === 0"
      (onClick)="addRow()"
      data-testid="product-vendor-transport-routes-add"
    />
  </div>

  @if (error(); as message) {
    <p-message severity="error" [closable]="true" (onClose)="error.set(null)">{{ message }}</p-message>
  }

  <p-table [value]="rows()" dataKey="key" [rowTrackBy]="trackByKey" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '52rem' }" [loading]="loading()">
    <ng-template #header>
      <tr>
        <th style="min-width: 15rem">{{ 'entities.ProductVendorTransportRoute.transportRoute' | translate }}</th>
        <th class="w-32 text-end" [pTooltip]="'transport-route.product.value-tooltip' | translate">{{ 'entities.ProductVendorTransportRoute.transitDays' | translate }}</th>
        <th class="w-36 text-end" [pTooltip]="'transport-route.product.value-tooltip' | translate">{{ 'entities.ProductVendorTransportRoute.fixedCost' | translate }}</th>
        <th class="w-36 text-end" [pTooltip]="'transport-route.product.value-tooltip' | translate">{{ 'entities.ProductVendorTransportRoute.costPerUnit' | translate }}</th>
        <th>{{ 'entities.ProductVendorTransportRoute.notes' | translate }}</th>
        <th class="w-32"></th>
      </tr>
    </ng-template>
    <ng-template #body let-row>
      @let busy = busyKey() === row.key;
      @let route = catalogOf(row);
      @let off = row.inactive;
      @let isDefaultRoute = defaultKey() === row.key;
      <tr
        [attr.data-testid]="'product-vendor-transport-route-row-' + (isNew(row) ? 'new' : route?.identifier)"
        [attr.data-inactive]="off"
        [attr.data-default]="isDefaultRoute"
      >
        <td [class.opacity-50]="off">
          @if (isNew(row)) {
            <p-select
              [options]="routeOptions(row)"
              optionLabel="identifier"
              optionValue="id"
              [ngModel]="row.transportRouteId"
              (ngModelChange)="update(row, { transportRouteId: $event })"
              size="small"
              appendTo="body"
              styleClass="w-full"
              data-testid="product-vendor-transport-route-select"
            >
              <ng-template #selectedItem let-item>
                @if (item) {
                  <span class="inline-flex items-center gap-2"><i [class]="routeIcon(item)"></i>{{ item.identifier }}</span>
                }
              </ng-template>
              <ng-template #item let-item>
                <span class="inline-flex items-center gap-2"><i [class]="routeIcon(item)"></i><span class="font-medium">{{ item.identifier }}</span><span class="text-muted-color">{{ item.description }}</span></span>
              </ng-template>
            </p-select>
          } @else {
            <span class="inline-flex flex-wrap items-center gap-2">
              <i [class]="iconOf(row)" class="text-muted-color"></i>
              <span class="font-medium" [class.line-through]="off">{{ route?.identifier }}</span>
              <span class="text-muted-color">{{ route?.description }}</span>
              <button
                type="button"
                class="h-7 w-7 rounded hover:bg-surface-200 dark:hover:bg-surface-700 disabled:cursor-default"
                [disabled]="off || busy"
                [pTooltip]="defaultTip(row)"
                tooltipPosition="top"
                (click)="toggleDefault(row)"
                data-testid="product-vendor-transport-route-default"
              >
                <i class="fa fa-star" [class.text-amber-500]="isDefaultRoute" [class.text-surface-300]="!isDefaultRoute"></i>
              </button>
              @if (off) {
                <span class="rounded bg-surface-200 px-1.5 text-xs text-surface-700 dark:bg-surface-700 dark:text-surface-200">{{ 'transport-route.product.inactive' | translate }}</span>
              }
              @if (!row.vendor) {
                <span class="rounded bg-sky-100 px-1.5 text-xs text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">{{ 'transport-route.product.only-product' | translate }}</span>
              } @else if (overridesValues(row)) {
                <span class="rounded bg-amber-100 px-1.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">{{ 'transport-route.product.overridden' | translate }}</span>
              }
            </span>
          }
        </td>
        <td class="text-end" [class.opacity-50]="off">
          <p-inputnumber
            [ngModel]="row.transitDays"
            (ngModelChange)="update(row, { transitDays: $event })"
            [min]="0"
            [maxFractionDigits]="0"
            [locale]="locale()"
            [placeholder]="fallbackTransit(row) != null ? '' + fallbackTransit(row) : ''"
            [disabled]="off"
            size="small"
            inputStyleClass="w-24 text-right"
            data-testid="product-vendor-transport-route-transit"
          />
        </td>
        <td class="text-end" [class.opacity-50]="off">
          <p-inputnumber
            [ngModel]="row.fixedCost"
            (ngModelChange)="update(row, { fixedCost: $event })"
            [min]="0"
            [minFractionDigits]="2"
            [maxFractionDigits]="4"
            mode="currency"
            [currency]="currencyCode()"
            [locale]="locale()"
            [placeholder]="moneyPlaceholder(row.vendor?.fixedCost)"
            [disabled]="off"
            size="small"
            inputStyleClass="w-28 text-right"
            data-testid="product-vendor-transport-route-fixed"
          />
        </td>
        <td class="text-end" [class.opacity-50]="off">
          <p-inputnumber
            [ngModel]="row.costPerUnit"
            (ngModelChange)="update(row, { costPerUnit: $event })"
            [min]="0"
            [minFractionDigits]="2"
            [maxFractionDigits]="4"
            mode="currency"
            [currency]="currencyCode()"
            [locale]="locale()"
            [placeholder]="moneyPlaceholder(row.vendor?.costPerUnit)"
            [disabled]="off"
            size="small"
            inputStyleClass="w-28 text-right"
            data-testid="product-vendor-transport-route-per-unit"
          />
        </td>
        <td [class.opacity-50]="off">
          <input pInputText type="text" class="w-full p-inputtext-sm" [disabled]="off" [ngModel]="row.notes" (ngModelChange)="update(row, { notes: $event })" data-testid="product-vendor-transport-route-notes" />
        </td>
        <td class="whitespace-nowrap text-end">
          @if (isDirty(row)) {
            <p-button icon="fa fa-check" severity="success" size="small" [text]="true" [rounded]="true" [loading]="busy" [disabled]="!row.transportRouteId" tooltipPosition="left" [pTooltip]="'common.save' | translate" (onClick)="saveRow(row)" data-testid="product-vendor-transport-route-save" />
            <p-button icon="fa fa-rotate-left" severity="secondary" size="small" [text]="true" [rounded]="true" [disabled]="busy" tooltipPosition="left" [pTooltip]="'transport-route.address.revert' | translate" (onClick)="revertRow(row)" data-testid="product-vendor-transport-route-revert" />
          }
          @if (!isNew(row)) {
            <!-- one ban icon for both states: grey = switched on, click switches off · green = switched off, click brings it back -->
            <p-button
              icon="fa fa-ban"
              [severity]="off ? 'success' : 'secondary'"
              size="small"
              [text]="true"
              [rounded]="true"
              [disabled]="busy"
              tooltipPosition="left" [pTooltip]="(off ? 'transport-route.product.activate' : 'transport-route.product.deactivate') | translate"
              (onClick)="toggleInactive(row)"
              data-testid="product-vendor-transport-route-inactive"
            />
          }
          <!-- back to the vendor only where there are product values; a star or a switch-off has its own button -->
          @if (row.saved && (!row.vendor || overridesValues(row))) {
            <p-button
              [icon]="row.vendor ? 'fa fa-arrow-rotate-left' : 'fa fa-trash'"
              [severity]="row.vendor ? 'secondary' : 'danger'"
              size="small"
              [text]="true"
              [rounded]="true"
              [disabled]="busy"
              tooltipPosition="left" [pTooltip]="(row.vendor ? 'transport-route.product.back-to-vendor' : 'transport-route.product.remove-own') | translate"
              (onClick)="backToVendor(row)"
              data-testid="product-vendor-transport-route-back"
            />
          }
        </td>
      </tr>
    </ng-template>
    <ng-template #emptymessage>
      <tr>
        <td colspan="6" class="text-sm text-muted-color">{{ 'transport-route.product.none' | translate }}</td>
      </tr>
    </ng-template>
  </p-table>
</section>
